// Per-sport chart config for the shared PlayerGameLogChart component.
//
// Each sport maps market keys to:
//   primary:  gameLog field name (string) OR accessor (game => number)
//             used for the main bar heights
//   overlays: array of extra series to render alongside the primary. Each entry:
//     { field, label, color?, layout: 'sameChart' | 'stacked' }
//     - 'sameChart' adds the overlay as a line dataset on the primary axis
//       (only appropriate when scales are similar, e.g. receptions vs targets).
//     - 'stacked' renders a separate mini bar chart below the primary chart
//       with its own left-side Y axis (use when scales differ wildly, e.g.
//       rush yds vs rush attempts).
//   isBinary: true when the market resolves to hit/miss per game rather than a
//             numeric line comparison (e.g. anytime TD, first TD).
//   isFirstScorer: true for "first X" markets — bar height 1 if the player
//             scored the game's first for their team, else 0. Overrides
//             lineValue with an implicit target of 1.
//
// The component looks up CHART_CONFIG[sport][marketKey] on every render and
// falls back to a no-config render (bars only, no overlays) when missing.

export const CHART_CONFIG = {
  nfl: {
    // NFL markets carry no overlays — the secondary counts sit on very
    // different scales from yardage totals and read poorly when overlaid.
    // Instead, `companions` lists related stat fields that show inside the
    // bar tooltip so hovering a game surfaces every relevant number for
    // that outing without cluttering the chart.
    // Passing
    player_pass_yds: {
      label: 'Pass Yds', primary: 'passingYards',
      companions: [
        { field: 'passingCompletions', label: 'Comp' },
        { field: 'passingAttempts', label: 'Att' },
        { field: 'passingTouchdowns', label: 'TD' },
        { field: 'passingInterceptions', label: 'INT' },
      ],
    },
    player_pass_tds: {
      label: 'Pass TDs', primary: 'passingTouchdowns',
      companions: [
        { field: 'passingYards', label: 'Yds' },
        { field: 'passingCompletions', label: 'Comp' },
        { field: 'passingAttempts', label: 'Att' },
        { field: 'passingInterceptions', label: 'INT' },
      ],
    },
    player_pass_completions: {
      label: 'Completions', primary: 'passingCompletions',
      companions: [
        { field: 'passingAttempts', label: 'Att' },
        { field: 'passingYards', label: 'Yds' },
        { field: 'passingTouchdowns', label: 'TD' },
      ],
    },
    player_pass_attempts: {
      label: 'Pass Att', primary: 'passingAttempts',
      companions: [
        { field: 'passingCompletions', label: 'Comp' },
        { field: 'passingYards', label: 'Yds' },
        { field: 'passingTouchdowns', label: 'TD' },
      ],
    },
    player_pass_interceptions: {
      label: 'INTs', primary: 'passingInterceptions',
      companions: [
        { field: 'passingAttempts', label: 'Att' },
        { field: 'passingYards', label: 'Yds' },
      ],
    },
    // Rushing
    player_rush_yds: {
      label: 'Rush Yds', primary: 'rushingYards',
      companions: [
        { field: 'rushingAttempts', label: 'Att' },
        { field: 'yardsPerRushAttempt', label: 'YPC' },
        { field: 'rushingTouchdowns', label: 'TD' },
        { field: 'longRushing', label: 'Long' },
      ],
    },
    player_rush_attempts: {
      label: 'Rush Att', primary: 'rushingAttempts',
      companions: [
        { field: 'rushingYards', label: 'Yds' },
        { field: 'yardsPerRushAttempt', label: 'YPC' },
        { field: 'rushingTouchdowns', label: 'TD' },
        { field: 'longRushing', label: 'Long' },
      ],
    },
    player_rush_tds: {
      label: 'Rush TDs', primary: 'rushingTouchdowns',
      companions: [
        { field: 'rushingAttempts', label: 'Att' },
        { field: 'rushingYards', label: 'Yds' },
        { field: 'longRushing', label: 'Long' },
      ],
    },
    player_rush_longest: {
      label: 'Long Rush', primary: 'longRushing',
      companions: [
        { field: 'rushingAttempts', label: 'Att' },
        { field: 'rushingYards', label: 'Yds' },
      ],
    },
    // Receiving
    player_receptions: {
      label: 'Receptions', primary: 'receptions',
      companions: [
        { field: 'receivingTargets', label: 'Tgt' },
        { field: 'receivingYards', label: 'Yds' },
        { field: 'receivingTouchdowns', label: 'TD' },
        { field: 'longReception', label: 'Long' },
      ],
    },
    player_reception_yds: {
      label: 'Rec Yds', primary: 'receivingYards',
      companions: [
        { field: 'receptions', label: 'Rec' },
        { field: 'receivingTargets', label: 'Tgt' },
        { field: 'yardsPerReception', label: 'YPR' },
        { field: 'receivingTouchdowns', label: 'TD' },
        { field: 'longReception', label: 'Long' },
      ],
    },
    player_reception_tds: {
      label: 'Rec TDs',
      primary: 'receivingTouchdowns',
    },
    // Combo market: rush yards + reception yards. Primary is a function so
    // it works even when the game log doesn't carry a pre-computed field.
    player_rush_reception_yds: {
      label: 'Ru+Rec Yds',
      primary: (g) => (g.rushingYards || 0) + (g.receivingYards || 0),
      companions: [
        { field: 'rushingYards', label: 'RuYds' },
        { field: 'rushingAttempts', label: 'RuAtt' },
        { field: 'receivingYards', label: 'ReYds' },
        { field: 'receptions', label: 'Rec' },
      ],
    },
    // Kicking: field goals made per game.
    player_field_goals: {
      label: 'FG Made', primary: 'fieldGoalsMade',
      companions: [
        { field: 'fieldGoalAttempts', label: 'Att' },
        { field: 'fieldGoalPct', label: 'Pct' },
        { field: 'longFieldGoalMade', label: 'Long' },
        { field: 'extraPointsMade', label: 'XPM' },
        { field: 'extraPointAttempts', label: 'XPA' },
      ],
    },
    player_reception_longest: {
      label: 'Long Rec',
      primary: 'longReception',
    },
    // Touchdowns (any / total). The gameLog payload only tells us whether
    // the player scored *any* TD in a game, not which was first or last.
    // player_1st_td / player_last_td intentionally have no entry here so
    // the shared chart renders nothing rather than mislabeling every scored
    // game as a first/last TD. Add them back once the feed carries scoring
    // order.
    player_anytime_td: {
      label: 'Anytime TD',
      primary: (g) => ((g.rushingTouchdowns || 0) + (g.receivingTouchdowns || 0) + (g.passingTouchdowns || 0)) > 0 ? 1 : 0,
      isBinary: true,
    },
    player_tds_over: {
      label: 'Total TDs',
      primary: (g) => (g.rushingTouchdowns || 0) + (g.receivingTouchdowns || 0) + (g.passingTouchdowns || 0),
    },
  },
  // Other sports get filled in as we migrate them to the shared component.
  // The component works with an empty entry — it just renders bars using no
  // overlays and expects the caller to pass an explicit primary field.
};

// Resolve the config entry for (sport, marketKey). Strips a `player_` prefix
// as a soft alias so callers can pass either form.
export const getChartConfig = (sport, marketKey) => {
  if (!sport || !marketKey) return null;
  const sportMap = CHART_CONFIG[sport];
  if (!sportMap) return null;
  return sportMap[marketKey] || sportMap[`player_${marketKey}`] || null;
};

// Pick the market's "primary" O/U line point — used by the game-log chart
// to draw the dashed reference line. Uses the same three-step fallback the
// PlayerOddsTable "onLineChange" effect uses so the chart's reference line
// matches whatever line the panel is highlighting:
//   1. Line with the most books flagged isMain / over_isMain / under_isMain
//      / yes_isMain.
//   2. Line offered by the most books.
//   3. First non-special line.
export const getPrimaryLinePoint = (market) => {
  if (!market || !Array.isArray(market.lines) || market.lines.length === 0) return null;

  let val = null;
  let maxMain = 0;
  market.lines.forEach(line => {
    if (line.isSpecial) return;
    let mainCount = 0;
    if (line.books) {
      Object.values(line.books).forEach(b => {
        if (b?.isMain === true || b?.over_isMain === true || b?.under_isMain === true || b?.yes_isMain === true) {
          mainCount++;
        }
      });
    }
    if (mainCount > maxMain) { maxMain = mainCount; val = line.point; }
  });

  if (val === null) {
    let maxBooks = 0;
    market.lines.forEach(line => {
      if (line.isSpecial) return;
      const cnt = line.books ? Object.keys(line.books).length : 0;
      if (cnt > maxBooks) { maxBooks = cnt; val = line.point; }
    });
  }

  if (val === null) {
    const first = market.lines.find(l => !l.isSpecial);
    val = first?.point ?? null;
  }
  return val;
};
